import { useSuspenseQueries } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { hasCustodialFundingConfirmed, hasOnboardingCompleted } from './instantPayEnrollment';
import { TakerMarket } from './useTakerMarkets';

type InstantPayStatsOperation = paths['/maker-organizations/{makerOrganizationUuid}/tokenized-invoices-stats']['get'];
type InstantPayStatsResponse = InstantPayStatsOperation['responses']['200']['content']['application/json'];
export type InstantPayStat = InstantPayStatsResponse['data'][number];

/**
 * One substitution statement, tagged with the customer it was fetched for and the supplier division's name.
 */
export interface SubstitutionRecord extends InstantPayStat {
  makerOrganizationUuid: string;
  customerName: string;
  divisionName: string | null;
}

/**
 * A customer with at least one supplier division live on Instant Pay; the page requests its stats.
 */
export interface SubstitutionCustomer {
  makerOrganizationUuid: string;
  name: string;
  /** Every taker market with this customer, used to name a record's division even after it left Instant Pay. */
  takerMarkets: TakerMarket[];
}

const isLiveDivision = (takerMarket: TakerMarket) =>
  hasOnboardingCompleted(takerMarket.instantPay) && hasCustodialFundingConfirmed(takerMarket.instantPay);

/**
 * Customers that can have substitution records: those with a supplier division that finished
 * onboarding and confirmed custodial funding. Like the page guard, `isEnabled` is ignored so a
 * division disabled after going live keeps its past records.
 */
export const getSubstitutionCustomers = (takerMarkets: TakerMarket[]): SubstitutionCustomer[] => {
  const byCustomer = new Map<string, SubstitutionCustomer & { hasLiveDivision: boolean }>();

  for (const takerMarket of takerMarkets) {
    const customer = byCustomer.get(takerMarket.makerOrganizationUuid) ?? {
      makerOrganizationUuid: takerMarket.makerOrganizationUuid,
      name: takerMarket.makerOrganizationName,
      takerMarkets: [],
      hasLiveDivision: false,
    };

    customer.takerMarkets.push(takerMarket);
    customer.hasLiveDivision ||= isLiveDivision(takerMarket);
    byCustomer.set(takerMarket.makerOrganizationUuid, customer);
  }

  return [...byCustomer.values()]
    .filter((customer) => customer.hasLiveDivision)
    .map(({ hasLiveDivision: _hasLiveDivision, ...customer }) => customer)
    .sort((a, b) => a.name.localeCompare(b.name));
};

const fetchInstantPayStats = async ({
  makerOrganizationUuid,
  startDate,
  endDate,
}: {
  makerOrganizationUuid: string;
  startDate?: string;
  endDate?: string;
}): Promise<InstantPayStat[]> => {
  const searchParams: InstantPayStatsOperation['parameters']['query'] = {
    type: 'substitution',
    ...(startDate && endDate ? { startDate, endDate } : {}),
  };

  const response = await apiClient
    .get(`api/supplier-experience/maker-organizations/${makerOrganizationUuid}/tokenized-invoices-stats`, {
      searchParams,
    })
    .json<InstantPayStatsResponse>();

  return response.data;
};

interface UseSubstitutionRecordsParams {
  customers: SubstitutionCustomer[];
  /** Inclusive `YYYY-MM-DD` bounds on the Instant Pay date. Omit both for all time. */
  startDate?: string;
  endDate?: string;
}

/**
 * Substitution records across the given customers, newest first. SEA's stats route is per customer
 * and not paginated, so this makes one request per customer and the page filters the merged rows.
 */
const useSubstitutionRecords = ({ customers, startDate, endDate }: UseSubstitutionRecordsParams) =>
  useSuspenseQueries({
    queries: customers.map((customer) => ({
      queryKey: ['instant-pay-substitution-stats', customer.makerOrganizationUuid, startDate, endDate],
      queryFn: () =>
        fetchInstantPayStats({ makerOrganizationUuid: customer.makerOrganizationUuid, startDate, endDate }),
    })),
    combine: (results): SubstitutionRecord[] =>
      results
        .flatMap(({ data }, index) => {
          const customer = customers[index];

          return data.map((stat) => ({
            ...stat,
            makerOrganizationUuid: customer.makerOrganizationUuid,
            customerName: customer.name,
            divisionName:
              customer.takerMarkets.find((takerMarket) => takerMarket.takerDivisionUuid === stat.takerUuid)
                ?.takerDivisionName ??
              stat.supplierName ??
              null,
          }));
        })
        // Reason: YYYY-MM-DD strings sort correctly as text; each customer's rows already come newest first.
        .sort((a, b) => b.instantPayDate.localeCompare(a.instantPayDate)),
  });

export default useSubstitutionRecords;
